import { useRef, useState } from "react";
import { CalendarPlus, ExternalLink } from "lucide-react";
import { resolveSessionCalendarAffordance } from "../lib/google-calendar";
import { AddToCalendarDialog } from "./add-to-calendar-dialog";

// Per-surface visual treatments so each surface keeps its existing look. Every
// interactive element carries a visible focus-visible ring + AA-contrast slate
// tokens. `openMuted` (cancelled Open, spec §8 E-CANCEL) reduces emphasis.
const VARIANTS = {
  "card-icon": {
    open: "relative z-10 inline-flex flex-shrink-0 cursor-pointer items-center justify-center gap-1 rounded-full border border-slate-200 p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-slate-100",
    openMuted:
      "relative z-10 inline-flex flex-shrink-0 cursor-pointer items-center justify-center gap-1 rounded-full border border-slate-200 p-1.5 text-slate-500 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:border-slate-700 dark:text-slate-400",
    add: "relative z-10 inline-flex flex-shrink-0 cursor-pointer items-center justify-center gap-1 rounded-full border border-slate-200 p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-slate-100",
    label: false,
  },
  "card-labeled": {
    open: "inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700",
    openMuted:
      "inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:border-slate-700 dark:text-slate-400",
    add: "inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-700",
    label: true,
    openLabel: "Calendar",
    addLabel: "Add",
  },
  chip: {
    open: "inline-flex min-h-6 items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:border-blue-700/60 dark:bg-blue-900/30 dark:text-blue-200 dark:hover:bg-blue-900/50",
    openMuted:
      "inline-flex min-h-6 items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700",
    add: "inline-flex min-h-6 items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-700",
    label: true,
    openLabel: "View in Google Calendar",
    addLabel: "Add to calendar",
  },
  "list-icon": {
    open: "mt-1.5 mr-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-200",
    openMuted:
      "mt-1.5 mr-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:text-slate-400 dark:hover:bg-slate-700",
    add: "mt-1.5 mr-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-200",
    label: false,
  },
};

const stop = (event) => event.stopPropagation();

/**
 * Renders EXACTLY ONE prominent calendar affordance per surface/viewer, decided
 * by the pure resolveSessionCalendarAffordance selector, and routes the
 * de-emphasized "Add (again)" into either the surface's own kebab (kebabHosted —
 * the surface renders its own menu item and drives addOpen/onAddOpenChange) or a
 * muted-secondary button. Always opens the shared, focus-trapped
 * AddToCalendarDialog. No Google API call — pure builders + stored fields + the
 * viewer's own settings.
 *
 * @param kebabHosted  When true, the control does NOT render an inline Add
 *   trigger in `open` mode (the surface hosts its own kebab item); it still owns
 *   the dialog via the controlled addOpen/onAddOpenChange props.
 */
export function SessionCalendarControl({
  scheduler,
  viewerUid,
  slot,
  viewer = {},
  filenameBase,
  variant = "card-icon",
  timeLabel = null,
  addOpen,
  onAddOpenChange,
  kebabHosted = false,
}) {
  const isControlled = typeof onAddOpenChange === "function";
  const [internalOpen, setInternalOpen] = useState(false);
  const dialogOpen = isControlled ? Boolean(addOpen) : internalOpen;
  const setDialogOpen = isControlled ? onAddOpenChange : setInternalOpen;
  // The control-owned Add trigger (muted-secondary or prominent). When the
  // surface hosts its own kebab item (kebabHosted), this stays null and Radix's
  // default focus restoration applies to the surface's own trigger.
  const triggerRef = useRef(null);

  const aff = resolveSessionCalendarAffordance(scheduler, viewerUid, slot, viewer);
  if (aff.mode === "none") {
    return null;
  }

  const v = VARIANTS[variant] || VARIANTS["card-icon"];
  const title = scheduler?.title || "session";

  // Explicitly restore focus to the control-owned trigger on close — Radix's
  // default restoration is unreliable under controlled open state (spec §4.3).
  const handleCloseAutoFocus = (event) => {
    if (triggerRef.current) {
      event.preventDefault();
      triggerRef.current.focus();
    }
  };

  const dialog = aff.entry ? (
    <AddToCalendarDialog
      open={dialogOpen}
      onOpenChange={setDialogOpen}
      entry={aff.entry}
      filenameBase={filenameBase}
      hasLinkedEvent={aff.hasLinkedEvent}
      openUrl={aff.openUrl}
      invited={aff.invited}
      timeLabel={timeLabel}
      onCloseAutoFocus={handleCloseAutoFocus}
    />
  ) : null;

  if (aff.mode === "open") {
    return (
      <>
        <a
          href={aff.openUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${title} in Google Calendar`}
          title="Open in Google Calendar"
          className={aff.cancelled ? v.openMuted : v.open}
          onClick={stop}
          onKeyDown={stop}
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          {v.label && <span>{v.openLabel}</span>}
        </a>
        {/* De-emphasized "Add again". When the surface hosts its own kebab item
            (kebabHosted), the inline trigger is suppressed but the dialog is
            still owned here (controlled). Otherwise a muted-secondary button
            keeps Add reachable for non-creator invitees with no kebab. */}
        {aff.entry && !kebabHosted && (
          <button
            ref={triggerRef}
            type="button"
            aria-haspopup="dialog"
            aria-expanded={dialogOpen}
            aria-label={`Add ${title} to calendar`}
            title="Add to calendar"
            className={v.add}
            onClick={(event) => {
              stop(event);
              setDialogOpen(true);
            }}
            onKeyDown={stop}
          >
            <CalendarPlus className="h-3.5 w-3.5" aria-hidden="true" />
            {v.label && <span>{v.addLabel}</span>}
          </button>
        )}
        {dialog}
      </>
    );
  }

  // mode === "add" — the prominent control is the dialog trigger.
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={dialogOpen}
        aria-label={`Add ${title} to calendar`}
        title="Add to calendar"
        className={v.add}
        onClick={(event) => {
          stop(event);
          setDialogOpen(true);
        }}
        onKeyDown={stop}
      >
        <CalendarPlus className="h-3.5 w-3.5" aria-hidden="true" />
        {v.label && <span>{v.addLabel}</span>}
      </button>
      {dialog}
    </>
  );
}
